import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Chip, SxProps, Theme, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  DrawerDescription,
  ImageGallery,
  MarketplaceDetailsSkeleton,
  ShipsFromNote,
  VendorCheckNotice,
  VendorLine,
  ViewDetailsSidebar,
} from '../../../components/_mui';
import {
  DOES_NOT_FIT_REMAINING_TEXT,
  getSupplierSwitchNoteText,
  getSwitchToSupplierLabel,
  OUT_OF_STOCK_LABEL,
  OVER_BUDGET_VARIANT_LABEL,
  SWAP_A_PICK_LABEL,
  VARIANT_CHIP_HEIGHT,
} from '../../../constants/marketplace';
import { PILL_BORDER_RADIUS } from '../../../constants/ui';
import {
  marketplaceAddToCartAction,
  marketplaceProductRequestAction,
  marketplaceRemoveFromCartAction,
  marketplaceReplaceCartAction,
  marketplaceSwapStartAction,
} from '../../../store/actions/marketplace';
import {
  selectMarketplaceCart,
  selectMarketplaceCatalogItem,
  selectMarketplaceProductDetails,
  selectMarketplaceProductStatus,
  selectSupplierLock,
} from '../../../store/selectors/marketplace';
import {
  IMarketplaceCartLine,
  IMarketplaceProductDetails,
  IMarketplaceVariant,
  MarketplaceDetailsStatusEnum,
} from '../../../types/marketplace';
import { DescriptionSourceEnum } from '../../../types/shell';
import { canAffordCredits, canAffordReplacement, getCartLineForProduct, isProductInCart } from '../../../util/credits';
import {
  buildCartLineFromDetails,
  buildVariantLabel,
  buildVariantOptionsLabel,
  getSelectableVariants,
  isOtherSupplier,
  isVariantSelectable,
} from '../../../util/marketplace';

interface IProps {
  productId: string | null;
  onClose: () => void;
}

const disabledChipSx = {
  opacity: 1,
  color: 'text.disabled',
  backgroundColor: ({ palette }: Theme) => alpha(palette.text.primary, 0.02),
  borderColor: 'divider',
};

// A pill that reads selected from a primary tint plus an inset ring, so the border width never
// changes and nothing shifts.
const variantChipSx: SxProps<Theme> = {
  height: VARIANT_CHIP_HEIGHT,
  minWidth: 56,
  borderRadius: PILL_BORDER_RADIUS,
  fontWeight: 500,
  backgroundColor: 'background.paper',
  '&.Mui-disabled': disabledChipSx,
};

const outOfStockChipSx: SxProps<Theme> = {
  ...variantChipSx,
  '&.Mui-disabled': { ...disabledChipSx, textDecoration: 'line-through' },
};

const pickedVariantChipSx: SxProps<Theme> = {
  ...variantChipSx,
  borderColor: 'primary.main',
  color: 'primary.main',
  backgroundColor: ({ palette }) => alpha(palette.primary.main, palette.action.selectedOpacity),
  boxShadow: ({ palette }) => `inset 0 0 0 1px ${palette.primary.main}`,
};

const detailsColumnSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 2 };

const variantSectionSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 1 };

const variantHeadingRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 0.75,
  flexWrap: 'wrap',
};

const variantRailSx: SxProps<Theme> = { display: 'flex', flexWrap: 'wrap', gap: 1 };

const supplierNoteSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1,
  py: 1.25,
  px: 1.5,
  borderRadius: 1,
  bgcolor: 'action.hover',
};

const supplierNoteIconSx: SxProps<Theme> = { fontSize: 18, color: 'text.secondary', flexShrink: 0, mt: 0.25 };

const supplierNoteTextSx: SxProps<Theme> = { textWrap: 'pretty' };

const failedStateSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  mt: 2,
  color: 'text.secondary',
};

const getProductKey = (product: IMarketplaceProductDetails) => product.product_id;

type TChipBlock = 'out-of-stock' | 'budget';

interface IVariantChipProps {
  variant: IMarketplaceVariant;
  isPicked: boolean;
  block?: TChipBlock;
  onPick: (variant: IMarketplaceVariant) => void;
}

const VariantChip: React.FC<IVariantChipProps> = ({ variant, isPicked, block, onPick }) => {
  const isOutOfStock = block === 'out-of-stock';

  const handleClick = useCallback(() => onPick(variant), [onPick, variant]);

  const chipSx = useMemo(() => {
    if (isOutOfStock) {
      return outOfStockChipSx;
    }

    return isPicked ? pickedVariantChipSx : variantChipSx;
  }, [isOutOfStock, isPicked]);

  const chip = (
    <Chip label={buildVariantLabel(variant)} variant="outlined" disabled={!!block} onClick={handleClick} sx={chipSx} />
  );

  if (!block) {
    return chip;
  }

  return (
    <Tooltip title={isOutOfStock ? OUT_OF_STOCK_LABEL : OVER_BUDGET_VARIANT_LABEL} placement="top" arrow>
      <span>{chip}</span>
    </Tooltip>
  );
};

export const MarketplaceDetailsDrawer: React.FC<IProps> = ({ productId, onClose }) => {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  const detailsSelector = useMemo(() => selectMarketplaceProductDetails(productId ?? undefined), [productId]);
  const statusSelector = useMemo(() => selectMarketplaceProductStatus(productId ?? undefined), [productId]);
  // The catalog row is already on screen, so the title and vendor show while details load.
  const catalogItemSelector = useMemo(() => selectMarketplaceCatalogItem(productId ?? undefined), [productId]);

  const details = useSelector(detailsSelector);
  const status = useSelector(statusSelector);
  const catalogItem = useSelector(catalogItemSelector);
  const cart = useSelector(selectMarketplaceCart);
  const supplierLock = useSelector(selectSupplierLock);

  const isFailed = status === MarketplaceDetailsStatusEnum.Failed;
  const isLoadingDetails = status === MarketplaceDetailsStatusEnum.Loading;
  const isAwaitingDetails = !!productId && !isFailed && (!details || isLoadingDetails);

  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [mainImage, setMainImage] = useState<string | null>(null);
  const [showVariantError, setShowVariantError] = useState(false);

  // Read through a ref so the reset runs only when the product changes, not on every cart edit.
  const cartRef = useRef<IMarketplaceCartLine[]>(cart);
  cartRef.current = cart;

  // Reopening a product already picked shows its variant selected, so the chips agree with the
  // "Remove from your picks" CTA.
  useEffect(() => {
    if (!productId) {
      return;
    }

    setSelectedVariantId(getCartLineForProduct(productId, cartRef.current)?.variant_id ?? null);
    setMainImage(null);
    setShowVariantError(false);
  }, [productId]);

  const handleRetry = useCallback(() => {
    if (productId) {
      dispatch(marketplaceProductRequestAction(productId));
    }
  }, [dispatch, productId]);

  const allVariants = useMemo(() => details?.variants ?? [], [details]);
  const selectableVariants = useMemo(
    () => (details ? getSelectableVariants(details).filter((variant) => !variant.isOutOfStock) : []),
    [details],
  );
  // A pick is forced only among in-pool variants; the section still shows when the rest exist.
  const hasVariantChoice = selectableVariants.length > 1;
  const isVariantSectionShown = allVariants.length > 1;

  const optionsLabel = useMemo(() => buildVariantOptionsLabel(allVariants), [allVariants]);

  const selectedVariant = useMemo(() => {
    if (hasVariantChoice) {
      return selectableVariants.find((variant) => variant.variant_id === selectedVariantId) ?? null;
    }

    return selectableVariants[0] ?? null;
  }, [hasVariantChoice, selectableVariants, selectedVariantId]);

  const inCart = !!details && isProductInCart(details.product_id, cart);
  const creditPrice = selectedVariant?.creditPrice ?? details?.creditPrice ?? 0;
  const vendor = details?.vendor ?? catalogItem?.vendor;
  const drawsFromWallet = allVariants[0]?.drawsFromWallet ?? true;
  const supplierCode = details?.supplierCode ?? catalogItem?.supplierCode;
  const isOther = !!details && !inCart && isOtherSupplier(supplierCode, supplierLock);
  const fitsWallet = !drawsFromWallet || (creditPrice > 0 && canAffordCredits(creditPrice, isOther ? [] : cart));
  const canAdd = !inCart && !isOther && fitsWallet;
  const canSwap = !inCart && !isOther && !canAdd && creditPrice > 0 && cart.length > 0;
  const canReplace = isOther && fitsWallet;
  const isCtaDisabled = !inCart && !canAdd && !canSwap && !canReplace && !hasVariantChoice;
  const optionHintColor = showVariantError ? 'error' : 'text.secondary';
  const optionHintWeight = showVariantError ? 'medium' : 'regular';

  const buildLine = useCallback(
    (product: IMarketplaceProductDetails, variant: IMarketplaceVariant): IMarketplaceCartLine => ({
      ...buildCartLineFromDetails(product, variant, catalogItem),
      variantLabel: hasVariantChoice ? buildVariantLabel(variant) : undefined,
    }),
    [hasVariantChoice, catalogItem],
  );

  const blockOf = useCallback(
    (variant: IMarketplaceVariant): TChipBlock | undefined => {
      if (variant.isOutOfStock) {
        return 'out-of-stock';
      }
      if (!isVariantSelectable(variant)) {
        return 'budget';
      }
      if (!details || !drawsFromWallet) {
        return undefined;
      }
      // A switch empties the cart, so only the whole wallet bounds the price.
      if (isOther) {
        return canAffordCredits(variant.creditPrice ?? 0, []) ? undefined : 'budget';
      }
      if (!inCart) {
        return undefined;
      }

      return canAffordReplacement(details.product_id, variant.creditPrice ?? 0, cart) ? undefined : 'budget';
    },
    [details, drawsFromWallet, isOther, inCart, cart],
  );

  const handleVariantPick = useCallback(
    (variant: IMarketplaceVariant) => {
      if (blockOf(variant)) {
        return;
      }

      setSelectedVariantId(variant.variant_id);
      setShowVariantError(false);
      if (variant.image_url) {
        setMainImage(variant.image_url);
      }

      const line = details && getCartLineForProduct(details.product_id, cart);

      if (details && line && line.variant_id !== variant.variant_id) {
        dispatch(marketplaceAddToCartAction(buildLine(details, variant)));
      }
    },
    [blockOf, details, cart, buildLine, dispatch],
  );

  const handleRemove = useCallback(() => {
    const line = getCartLineForProduct(details?.product_id ?? '', cart);

    if (line) {
      dispatch(marketplaceRemoveFromCartAction(line.variant_id));
    }
    onClose();
  }, [details?.product_id, cart, dispatch, onClose]);

  const handleAdd = useCallback(() => {
    if (!details) {
      return;
    }
    if (hasVariantChoice && !selectedVariant) {
      setShowVariantError(true);

      return;
    }
    if (!selectedVariant || !canAdd) {
      return;
    }

    dispatch(marketplaceAddToCartAction(buildLine(details, selectedVariant)));
    onClose();
  }, [details, hasVariantChoice, selectedVariant, canAdd, buildLine, dispatch, onClose]);

  const handleSwap = useCallback(() => {
    if (!details) {
      return;
    }
    if (hasVariantChoice && !selectedVariant) {
      setShowVariantError(true);

      return;
    }
    if (!selectedVariant) {
      return;
    }

    dispatch(marketplaceSwapStartAction(buildLine(details, selectedVariant)));
    onClose();
  }, [details, selectedVariant, hasVariantChoice, buildLine, dispatch, onClose]);

  const handleReplace = useCallback(() => {
    if (!details) {
      return;
    }

    if (hasVariantChoice && !selectedVariant) {
      setShowVariantError(true);

      return;
    }
    if (!selectedVariant || !canReplace) {
      return;
    }

    dispatch(marketplaceReplaceCartAction(buildLine(details, selectedVariant)));
    onClose();
  }, [details, hasVariantChoice, selectedVariant, canReplace, buildLine, dispatch, onClose]);

  const isBudgetBlocked = !inCart && !canAdd && !canReplace && creditPrice > 0;

  const ctaHint = useMemo(() => {
    if (showVariantError) {
      return 'Choose an option to add';
    }
    if (isBudgetBlocked) {
      return 'No room in your budget for another';
    }

    return null;
  }, [showVariantError, isBudgetBlocked]);

  const itemSupplierName = details?.supplierName || vendor;

  const supplierNoteText = useMemo(
    () => getSupplierSwitchNoteText(itemSupplierName, supplierLock?.name),
    [itemSupplierName, supplierLock?.name],
  );

  const renderDetails = useCallback(
    (product: IMarketplaceProductDetails) => (
      <Box sx={detailsColumnSx}>
        <ImageGallery images={product.images ?? []} alt={product.name} activeImage={mainImage} />

        <VendorLine vendor={vendor} />

        <DrawerDescription description={product.description} source={DescriptionSourceEnum.Marketplace} clamp />

        {isOther && (
          <Box role="note" sx={supplierNoteSx}>
            <InfoOutlinedIcon aria-hidden sx={supplierNoteIconSx} />
            <Typography variant="body2" color="text.primary" sx={supplierNoteTextSx}>
              {supplierNoteText}
            </Typography>
          </Box>
        )}

        {isVariantSectionShown && (
          <Box sx={variantSectionSx}>
            <Box sx={variantHeadingRowSx}>
              <Typography variant="body2" fontWeight="medium" color="text.primary">
                {optionsLabel}
              </Typography>
              <Typography variant="body2" color={optionHintColor} fontWeight={optionHintWeight}>
                {selectedVariant ? buildVariantLabel(selectedVariant) : '— select an option'}
              </Typography>
            </Box>
            <Box sx={variantRailSx}>
              {allVariants.map((variant) => (
                <VariantChip
                  key={variant.variant_id}
                  variant={variant}
                  isPicked={variant.variant_id === selectedVariant?.variant_id}
                  block={blockOf(variant)}
                  onPick={handleVariantPick}
                />
              ))}
            </Box>
          </Box>
        )}

        <ShipsFromNote vendor={product.supplierName ?? vendor} />
      </Box>
    ),
    [
      mainImage,
      vendor,
      isOther,
      supplierNoteText,
      isVariantSectionShown,
      allVariants,
      selectedVariant,
      optionHintColor,
      optionHintWeight,
      handleVariantPick,
      blockOf,
      optionsLabel,
    ],
  );

  // The hint shares the row with the CTA on desktop; on mobile the button spans the sheet, so
  // it stacks above instead.
  const footerRowSx: SxProps<Theme> = useMemo(
    () => ({
      display: 'flex',
      flexDirection: isMobileViewport ? 'column' : 'row',
      alignItems: isMobileViewport ? 'stretch' : 'center',
      justifyContent: 'flex-end',
      gap: isMobileViewport ? 1 : 1.5,
      width: '100%',
    }),
    [isMobileViewport],
  );

  const ctaHintSx: SxProps<Theme> = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      minWidth: 0,
      color: showVariantError ? 'error.main' : 'text.secondary',
    }),
    [showVariantError],
  );

  const ctaLabel = useMemo(() => {
    if (inCart) {
      return 'Remove from your picks';
    }
    if (isOther) {
      return getSwitchToSupplierLabel(itemSupplierName);
    }

    return canSwap ? SWAP_A_PICK_LABEL : 'Add to your picks';
  }, [inCart, isOther, itemSupplierName, canSwap]);

  const ctaAction = useMemo(() => {
    if (inCart) {
      return handleRemove;
    }
    if (isOther) {
      return handleReplace;
    }

    return canSwap ? handleSwap : handleAdd;
  }, [inCart, isOther, canSwap, handleRemove, handleReplace, handleSwap, handleAdd]);

  const isCtaOutlined = inCart || isOther;

  const footerButton = useMemo(
    () => (
      <Box sx={footerRowSx}>
        {ctaHint && (
          <Box sx={ctaHintSx}>
            <ErrorOutlineIcon sx={{ fontSize: 18, flexShrink: 0 }} />
            <Typography variant="body2" color="inherit">
              {ctaHint}
            </Typography>
          </Box>
        )}
        <Button
          variant={isCtaOutlined ? 'outlined' : 'contained'}
          color="primary"
          fullWidth={isMobileViewport}
          disabled={isCtaDisabled}
          title={canSwap ? DOES_NOT_FIT_REMAINING_TEXT : undefined}
          onClick={ctaAction}
        >
          {ctaLabel}
        </Button>
      </Box>
    ),
    [ctaHint, footerRowSx, ctaHintSx, isCtaOutlined, isCtaDisabled, canSwap, ctaAction, ctaLabel, isMobileViewport],
  );

  const headerContent = useMemo(() => {
    if (isFailed) {
      return (
        <Box sx={failedStateSx}>
          <ErrorOutlineIcon sx={{ fontSize: 18, flexShrink: 0 }} />
          <Typography variant="body2" color="inherit" sx={{ flex: 1, minWidth: 0 }}>
            Could not load options
          </Typography>
          <Button variant="text" color="primary" size="small" onClick={handleRetry}>
            Retry
          </Button>
        </Box>
      );
    }

    return isAwaitingDetails ? <MarketplaceDetailsSkeleton vendor={catalogItem?.vendor} /> : undefined;
  }, [isFailed, isAwaitingDetails, catalogItem?.vendor, handleRetry]);

  const footerContent = isAwaitingDetails ? <VendorCheckNotice /> : footerButton;
  const isFooterShown = !isFailed;

  return (
    <ViewDetailsSidebar<IMarketplaceProductDetails>
      open={!!productId}
      onClose={onClose}
      title={details?.name ?? catalogItem?.name ?? ''}
      items={details && !isAwaitingDetails ? [details] : []}
      renderItem={renderDetails}
      getItemKey={getProductKey}
      headerContent={headerContent}
      footerButton={isFooterShown ? footerContent : undefined}
      hideItemsCount
    />
  );
};
